import { useAtom } from "jotai";
import { useEffect, useRef, useState } from "react";
import { useIdtoApi } from "../hooks/useIdtoApi";
import { useEventEmitter } from "../hooks/useEventEmitter";
import HeaderLogo from "../Icon/HeaderLogo";
import type { LanguageOption } from "../store";
import { isInitModalOpenAtom, languageAtom, referenceExpiresAtAtom, referenceKeyAtom, tokenAtom, userIdAtom } from "../store";
import {
  ChevronDownIcon,
  DigilockerIcon,
  SecurityShieldIcon,
  SpinnerIcon
} from "./icons";
import { ArrowIcon, Button } from "./Overview";
import { useLanguage } from "../hooks/useLanguage";

export const DigilockerInit = () => {
  const [language, setLanguage] = useAtom(languageAtom);
  const { t } = useLanguage();
  const [userId,] = useAtom(userIdAtom);
  const [token,] = useAtom(tokenAtom);
  // const [isMuted, setIsMuted] = useAtom(isMutedAtom);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const languageDropdownRef = useRef<HTMLDivElement>(null);
  const [isDigilockerSessionInitiated, setIsDigilockerSessionInitiated] = useState(false);
  const [isReferenceReceived, setIsReferenceReceived] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const { initiateDigilockerSession, getDigilockerReference, isLoading, error, clearError } = useIdtoApi();
  const [referenceKey, setReferenceKey] = useAtom(referenceKeyAtom);
  const [, setReferenceExpiresAt] = useAtom(referenceExpiresAtAtom);
  const { emit } = useEventEmitter();
  const [, setIsInitModalOpen] = useAtom(isInitModalOpenAtom);

  const resetAllData = () => {
    setIsDigilockerSessionInitiated(false);
    setIsReferenceReceived(false);
    setCountdown(3);
    setReferenceKey("");
    setReferenceExpiresAt("");
    clearError();
    setIsInitModalOpen(false);
  };

  const handleInitiateDigilockerSession = async () => {
    if (isLoading || isDigilockerSessionInitiated) return;
    try {
      clearError()
      const response = await initiateDigilockerSession({
        user_id: userId,
        url: "https://demo.idto.ai/done",
      }, token);
      if (response?.url) {
        setIsDigilockerSessionInitiated(true);
        window.open(response?.url, '_blank');
        
        // Emit session initiated event
        emit('SESSION_INITIATED', {
          user_id: userId,
          redirect_url: response.url
        });
      }
    } catch (error) {
      console.error("Error initiating digilocker session:", error);
      
      // Emit error event
      emit('ERROR_OCCURRED', {
        user_id: userId,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        error_code: 'SESSION_INITIATION_FAILED'
      });
    }
  };

  const languageOptions: LanguageOption[] = [
    { label: t('languageEn'), value: "en" },
    { label: t('languageHi'), value: "hi" }
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target as Node)) {
        setShowLanguageDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Listen for postMessage from the opened DigiLocker window
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      // Check if the message contains code and codeVerifier
      if (event.data && event.data.code && event.data.codeVerifier) {
        try {
          const response = await getDigilockerReference({
            user_id: userId,
            code: event.data.code,
            code_verifier: event.data.codeVerifier,
          }, token);

          if (response.status === 'success') {
            setReferenceKey(response.reference_key);
            setReferenceExpiresAt(response.expires_at);
            setIsReferenceReceived(true);
            
            // Emit reference received event
            emit('REFERENCE_RECEIVED', {
              user_id: userId,
              reference_key: response.reference_key,
              expires_at: response.expires_at
            });
            
            // Emit session completed event
            emit('SESSION_COMPLETED', {
              user_id: userId
            });
          }
        } catch (error) {
          console.error('❌ Error getting reference data:', error);
          
          // Emit error event
          emit('ERROR_OCCURRED', {
            user_id: userId,
            error: error instanceof Error ? error.message : 'Unknown error occurred',
            error_code: 'REFERENCE_FETCH_FAILED'
          });
        }

        // Optionally, you can also set the session as completed here
        setIsDigilockerSessionInitiated(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [setReferenceKey, setReferenceExpiresAt, getDigilockerReference, userId, token]);

  // Auto-reset effect when reference is received
  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (isReferenceReceived) {
      // Start countdown immediately - show 3, then 2, then 1
      timer = setTimeout(() => {
        setCountdown(2);
      }, 1000);
      
      const timer2 = setTimeout(() => {
        setCountdown(1);
      }, 2000);
      
      // Reset after 3 seconds total
      const resetTimer = setTimeout(() => {
        // Emit session expired event before resetting
        if (referenceKey) {
          emit('SESSION_EXPIRED', {
            user_id: userId,
            reference_key: referenceKey
          });
        }
        resetAllData();
      }, 3000);
      
      return () => {
        if (timer) clearTimeout(timer);
        if (timer2) clearTimeout(timer2);
        if (resetTimer) clearTimeout(resetTimer);
      };
    }
  }, [isReferenceReceived]);

  // const toggleMute = () => {
  //   const newMutedState = !isMuted;
  //   setIsMuted(newMutedState);
  //   
  //   // Emit audio toggle event
  //   emit('AUDIO_TOGGLED', {
  //     is_muted: newMutedState
  //   });
  // };

  const handleLanguageChange = (newLanguage: LanguageOption) => {
    setLanguage(newLanguage);
    setShowLanguageDropdown(false);
    
    // Emit language change event
    emit('LANGUAGE_CHANGED', {
      language: newLanguage.label,
      language_code: newLanguage.value
    });
  };

  return (
    <div className="idto_digilocker_init_container idto_digilocker_init_container_full_height">
      <div className="idto_digilocker_init_header">
        <div className="idto_digilocker_init_logo_container">
          <HeaderLogo color="#0019FF" />
        </div>

        <div className="idto_digilocker_init_header_controls">
          {/* Language Selection */}
          <div className="idto_digilocker_init_language_dropdown" ref={languageDropdownRef}>
            <div
              className={`idto_digilocker_init_language_trigger ${showLanguageDropdown ? 'active' : ''}`}
              onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
            >
              <span className="idto_digilocker_init_language_text">{language.label}</span>
              <div className="idto_digilocker_init_language_arrow">
                <ChevronDownIcon />
              </div>
            </div>

            {showLanguageDropdown && (
              <div className="idto_digilocker_init_language_dropdown_menu">
                {languageOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`idto_digilocker_init_language_option ${language.value === option.value ? 'selected' : ''}`}
                    onClick={() => handleLanguageChange(option)}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Audio Control */}
          {/* <div className="idto_digilocker_init_audio_control" onClick={toggleMute}>
            <span className="idto_digilocker_init_audio_text">
              {isMuted ? t('unmute') : t('mute')}
            </span>
            <SpeakerIcon isMuted={isMuted} />
          </div> */}
        </div>
      </div>

      {isReferenceReceived ? (
        <div className="idto_digilocker_success_container">
          <div className="idto_digilocker_success_content">
            <img src="https://idto-sdk-bucket.s3.ap-south-1.amazonaws.com/success_gig.gif" alt={t('successGifAlt')} className="idto_digilocker_success_gif" />
                         <p className="idto_digilocker_countdown_text">{t('closingIn')} {countdown} {t('seconds')}</p>
          </div>
                     <div className="idto_digilocker_init_branding">
             <p className="idto_digilocker_init_branding_text">{t('secureAndEncrypted')}</p>
             <img
               src="https://idto-sdk-bucket.s3.ap-south-1.amazonaws.com/idto_logo.png"
               className="idto_digilocker_init_branding_logo"
               alt={t('idtoLogoAlt')}
             />
           </div>
        </div>
      ) : (
        <div className="idto_digilocker_init_content">
          <div className="idto_digilocker_init_main_content">
            <div className="idto_digilocker_init_info_section">
              <div className="idto_digilocker_init_icon_container">
                <DigilockerIcon />
              </div>
              <div className="idto_digilocker_init_text_section">
                <p className="idto_digilocker_init_title">{t('verifyAadhaar')}</p>
                <p className="idto_digilocker_init_description">
                  {t('verifyAadhaarDescription')}
                </p>
              </div>
            </div>

            <div className="idto_digilocker_init_visual_section">
              <div className="idto_digilocker_init_image_container"></div>

              <div className="idto_digilocker_init_security_badge">
                <div>
                  <SecurityShieldIcon />
                </div>
                <p className="idto_digilocker_init_security_text">{t('securedByGovt')}</p>
              </div>
            </div>
          </div>

          <div className="idto_digilocker_init_footer">
            <div className="idto_digilocker_init_button_container">
              <Button className="idto_digilocker_init_button" onClick={handleInitiateDigilockerSession}>
                {isLoading || isDigilockerSessionInitiated ? (
                  <div className="idto_digilocker_button_loader">
                    <SpinnerIcon />
                  </div>
                ) : (
                  <>
                    <p className="idto_digilocker_button_text">{t('continue')}</p>
                    <ArrowIcon />
                  </>
                )}
              </Button>
              {error && <p className="idto_digilocker_init_error_text">{error}</p>}
            </div>
            <div className="idto_digilocker_init_branding">
              <p className="idto_digilocker_init_branding_text">{t('secureAndEncrypted')}</p>
              <img
                src="https://idto-sdk-bucket.s3.ap-south-1.amazonaws.com/idto_logo.png"
                className="idto_digilocker_init_branding_logo"
                alt={t('idtoLogoAlt')}
              />
            </div>
          </div>
        </div>
      )
      }
    </div >
  );
};